import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../lib/auth";
import { useConfig } from "../lib/config";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const EMPTY_FORM = {
  id: null,
  nombre: "",
  email: "",
  password: "",
  role: "facultad",
  facultad_id: "",
  unidad_id: "",
};

export default function Usuarios() {
  const { user: me } = useAuth();
  const { t } = useTranslation();
  const { config } = useConfig();
  const [users, setUsers] = useState([]);
  const [facultades, setFacultades] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const facultadSingular = config.facultad_label_singular || "Facultad";
  const unidadSingular = config.unidad_label_singular || "Unidad";

  const ROLE_LABEL_I18N = {
    admin: t("usr.rol_admin"),
    facultad: facultadSingular,
    unidad: unidadSingular,
    directivo: t("usr.rol_directivo"),
  };

  const load = () => {
    Promise.all([api.get("/users"), api.get("/facultades"), api.get("/unidades")])
      .then(([u, f, un]) => {
        setUsers(u.data);
        setFacultades(f.data);
        setUnidades(un.data);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (u) => {
    setForm({
      id: u.id,
      nombre: u.nombre,
      email: u.email,
      password: "",
      role: u.role,
      facultad_id: u.facultad_id || "",
      unidad_id: u.unidad_id || "",
    });
    setShowModal(true);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const availableUnidades = form.facultad_id
    ? unidades.filter((u) => u.facultad_id === form.facultad_id)
    : unidades;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre,
        email: form.email,
        role: form.role,
        facultad_id: form.role === "facultad" || form.role === "unidad" ? form.facultad_id || null : null,
        unidad_id: form.role === "unidad" ? form.unidad_id || null : null,
      };
      if (form.password) payload.password = form.password;

      if (form.id) {
        await api.patch(`/users/${form.id}`, payload);
        toast.success(t("usr.toast_updated"));
      } else {
        if (!form.password) {
          toast.error(t("toast.password_required"));
          setSaving(false);
          return;
        }
        await api.post("/users", payload);
        toast.success(t("usr.toast_created"));
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("toast.error_save"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (u.id === me?.id) return toast.error(t("toast.no_self_delete"));
    if (!confirm(t("usr.confirm_delete", { name: u.nombre }))) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(t("usr.toast_deleted"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("toast.error_delete"));
    }
  };

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-10">
          <div>
            <div className="section-marker">{t("common.administration")}</div>
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter mt-2">
              {t("usr.page_title")}<span className="serif-em text-zinc-900">.</span>
            </h1>
          </div>
          <button onClick={openCreate} className="bg-zinc-950 text-white px-5 py-3 font-medium hover:bg-zinc-800 inline-flex items-center justify-center gap-2 w-full sm:w-auto" data-testid="btn-new-user">
            <Plus size={18} strokeWidth={1.5} /> {t("usr.btn_new")}
          </button>
        </div>

        <div className="grid grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-10 pb-8 sm:pb-10 border-b border-zinc-200">
          <div>
            <div className="section-marker">{t("usr.kpi_total")}</div>
            <div className="kpi-number text-4xl sm:text-6xl mt-2">{users.length}</div>
          </div>
          <div>
            <div className="section-marker">{t("usr.kpi_admins")}</div>
            <div className="kpi-number text-4xl sm:text-6xl mt-2">{users.filter(u => u.role === "admin").length}</div>
          </div>
          <div>
            <div className="section-marker">{t("usr.kpi_facultades")}</div>
            <div className="kpi-number text-4xl sm:text-6xl mt-2">{users.filter(u => u.role === "facultad").length}</div>
          </div>
        </div>

        {loading ? (
          <div className="text-zinc-500">{t("common.loading")}</div>
        ) : (
          <div className="border-t-2 border-zinc-950 overflow-x-auto">
            <table className="w-full min-w-[720px]" data-testid="users-table">
              <thead>
                <tr>
                  <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("usr.col_nombre")}</th>
                  <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("usr.col_email")}</th>
                  <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("usr.col_rol")}</th>
                  <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("usr.col_facultad")}</th>
                  <th className="py-3"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-zinc-100 row-hover" data-testid={`user-row-${u.id}`}>
                    <td className="py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-zinc-950 text-white flex items-center justify-center text-xs font-medium">
                          {(u.nombre || "?").slice(0, 1).toUpperCase()}
                        </div>
                        <span className="font-medium">
                          {u.nombre}
                          {u.id === me?.id && <span className="ml-2 text-xs text-zinc-400">{t("usr.you")}</span>}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 text-zinc-600">{u.email}</td>
                    <td className="py-4">
                      <span className="inline-flex items-center border border-zinc-200 px-2.5 py-1 text-xs font-medium">
                        {ROLE_LABEL_I18N[u.role] || u.role}
                      </span>
                    </td>
                    <td className="py-4 text-zinc-600">{u.facultad_nombre || "—"}</td>
                    <td className="py-4 text-right space-x-2">
                      <button onClick={() => openEdit(u)} className="text-zinc-400 hover:text-zinc-950 p-2" data-testid={`edit-${u.id}`}>
                        <Pencil size={16} strokeWidth={1.5} />
                      </button>
                      <button onClick={() => remove(u)} className="text-zinc-400 hover:text-red-700 p-2 disabled:opacity-30" disabled={u.id === me?.id} data-testid={`del-user-${u.id}`}>
                        <Trash2 size={16} strokeWidth={1.5} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
            <div className="bg-white w-full max-w-lg p-6 sm:p-8 border border-zinc-200 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="user-modal">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="section-marker">{form.id ? t("usr.modal_edit") : t("usr.modal_new")}</div>
                  <h2 className="text-3xl font-medium tracking-tight mt-1">
                    {form.id ? t("usr.update_title") : t("usr.create_title")}
                  </h2>
                </div>
                <button onClick={() => setShowModal(false)} className="text-zinc-400 hover:text-zinc-950"><X size={20} strokeWidth={1.5} /></button>
              </div>
              <form onSubmit={submit} className="space-y-4" data-testid="user-form">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("usr.field_nombre")}</label>
                  <input required value={form.nombre} onChange={set("nombre")} className="w-full border border-zinc-300 px-4 py-3" data-testid="user-nombre" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("usr.field_email")}</label>
                  <input required type="email" value={form.email} onChange={set("email")} className="w-full border border-zinc-300 px-4 py-3" data-testid="user-email" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                    {t("usr.field_password")} {form.id && <span className="text-zinc-400 normal-case font-normal">{t("usr.field_password_hint")}</span>}
                  </label>
                  <input type="password" minLength={form.id ? 0 : 6} value={form.password} onChange={set("password")} className="w-full border border-zinc-300 px-4 py-3" data-testid="user-password" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("usr.field_rol")}</label>
                  <select value={form.role} onChange={set("role")} className="w-full border border-zinc-300 px-4 py-3 bg-white" data-testid="user-role">
                    <option value="admin">{t("usr.rol_admin")}</option>
                    <option value="facultad">{facultadSingular}</option>
                    {config.enable_unidades && (
                      <option value="unidad">{unidadSingular}</option>
                    )}
                    <option value="directivo">{t("usr.rol_directivo")}</option>
                  </select>
                </div>
                {(form.role === "facultad" || form.role === "unidad") && (
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{facultadSingular}</label>
                    <select required value={form.facultad_id} onChange={set("facultad_id")} className="w-full border border-zinc-300 px-4 py-3 bg-white" data-testid="user-facultad">
                      <option value="">{t("usr.select_default")}</option>
                      {facultades.map((f) => (
                        <option key={f.id} value={f.id}>{f.nombre}</option>
                      ))}
                    </select>
                  </div>
                )}
                {form.role === "unidad" && (
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{unidadSingular}</label>
                    <select required value={form.unidad_id} onChange={set("unidad_id")} className="w-full border border-zinc-300 px-4 py-3 bg-white" data-testid="user-unidad">
                      <option value="">{t("usr.select_default")}</option>
                      {availableUnidades.map((u) => (
                        <option key={u.id} value={u.id}>{u.nombre}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="flex gap-3 pt-3">
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 border border-zinc-300 px-6 py-3 font-medium hover:bg-zinc-50">{t("common.cancel")}</button>
                  <button type="submit" disabled={saving} className="flex-1 bg-zinc-950 text-white px-6 py-3 font-medium hover:bg-zinc-800 disabled:opacity-50" data-testid="user-submit">
                    {saving ? t("usr.saving") : form.id ? t("usr.update") : t("usr.create")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
